'use client';
import React, { useState } from 'react';

const faqs = [
{
  q: 'What are alternative investments?',
  a: 'Alternative investments are asset classes beyond traditional stocks, bonds, and cash. They include private equity, private credit, hedge funds, real estate, infrastructure, and commodities. These investments typically offer lower correlation to public markets, potential for enhanced returns, and diversification benefits that can improve overall portfolio risk-adjusted performance.'
},
{
  q: 'Why should my clients consider alternatives?',
  a: 'Institutional investors — including pension funds, endowments, and sovereign wealth funds — have allocated 20-40% of their portfolios to alternatives for decades. The data consistently shows that properly allocated alternatives can reduce portfolio volatility, enhance risk-adjusted returns, provide inflation protection, and generate income streams uncorrelated with public markets.'
},
{
  q: 'What is the minimum investment for alternative strategies?',
  a: 'Through our platform, we have negotiated institutional access terms that significantly lower traditional minimums. While top-tier managers typically require $1M+ minimums for direct investment, our aggregated platform approach allows qualified clients to access these strategies at substantially reduced entry points.'
},
{
  q: 'How does Timber Point Capital Management select managers?',
  a: 'Our manager selection process combines quantitative screening with deep qualitative due diligence. We evaluate track records, risk management frameworks, operational infrastructure, team stability, and alignment of interests. Only managers who pass our rigorous multi-stage review process are included in our curated universe.'
},
{
  q: 'How are alternative investments integrated into existing portfolios?',
  a: 'We work alongside your existing practice to analyze current portfolio construction, identify appropriate alternative allocations, and seamlessly integrate new positions. Our team provides ongoing monitoring, rebalancing recommendations, and client-ready reporting — all designed to complement your existing workflow.'
},
{
  q: 'What types of alternative investments does TPCM offer access to?',
  a: 'Our platform provides access to a broad spectrum of alternative strategies including: Private Credit (direct lending, mezzanine, distressed), Private Equity (buyout, growth, venture), Hedge Funds (long/short equity, macro, arbitrage), Real Assets (real estate, infrastructure, commodities), and Long/Short Strategies.'
},
{
  q: 'How does TPCM support advisors operationally?',
  a: 'We handle the operational complexity of alternatives — including subscription documents, capital calls, distributions, K-1 coordination, and performance reporting. Our streamlined processes reduce the administrative burden on your practice, allowing you to focus on client relationships and business development.'
},
{
  q: 'Is Timber Point Capital Management a fiduciary?',
  a: 'Yes. Timber Point Capital Management operates as a registered investment advisor with a fiduciary duty to act in the best interests of clients at all times. Investment advice is offered through Fortis Capital Advisors, LLC. We place client interests above all else in every recommendation and decision.'
}];


export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <section id="faqs" style={{ backgroundColor: '#0f2240' }}>
      {/* Black banner header */}
      <div style={{ backgroundColor: '#0f2240', borderBottom: '2px solid #c9a84c' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-12">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-0.5" style={{ backgroundColor: '#c9a84c' }} />
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#c9a84c', letterSpacing: '0.2em' }}>
              FAQ&apos;s
            </span>
          </div>
          <h2 className="font-black text-white" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: '1.15', fontFamily: 'Lato, sans-serif' }}>
            Frequently Asked Questions
          </h2>
        </div>
      </div>
      {/* Video Feature Section — split two-column */}
      <div className="w-full" style={{ backgroundColor: '#0f2240' }}>
        <div className="max-w-5xl mx-auto">
        <div className="flex flex-col lg:flex-row" style={{ minHeight: '320px' }}>
          {/* Left: Aerial forest thumbnail with play button */}
          <div
              className="relative flex-1 flex items-center justify-center cursor-pointer group overflow-hidden"
              style={{ minHeight: '280px', backgroundColor: '#1a3a1a' }}
              onClick={() => setVideoOpen(true)}>
            
            {/* Forest background image */}
            <img
                src="https://images.unsplash.com/photo-1627063652977-ad8190e09cd3"
                alt="Aerial view of dense forest with winding road through trees"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ filter: 'brightness(0.75)' }} />
            
            {/* Overlay */}
            <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.18)' }} />

            {/* Play button + label */}
            <div className="relative z-10 flex flex-col items-center gap-3">
              <div
                  className="flex items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-110"
                  style={{
                    width: '72px',
                    height: '72px',
                    backgroundColor: '#c9a84c',
                    boxShadow: '0 4px 24px rgba(201,168,76,0.45)'
                  }}>
                
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <polygon points="6,4 20,12 6,20" />
                </svg>
              </div>
              <span
                  className="font-bold tracking-widest text-white text-sm uppercase"
                  style={{ letterSpacing: '0.18em', textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>
                
                WATCH VIDEO
              </span>
            </div>

            {/* Bottom-left label */}
            <div className="absolute bottom-4 left-5 z-10">
              <p className="font-bold text-white text-sm" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                TPCM Alternatives Introduction
              </p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)', textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                Timber Point Capital Management
              </p>
            </div>
          </div>

          {/* Right: Dark navy info panel */}
          <div
              className="flex-1 flex flex-col justify-center px-10 py-12"
              style={{ backgroundColor: '#0d1e38', maxWidth: '480px' }}>
            
            {/* Gold decorative rule */}
            <div className="w-10 h-0.5 mb-6" style={{ backgroundColor: '#c9a84c' }} />

            {/* Title */}
            <h3
                className="font-black text-white mb-4"
                style={{ fontSize: 'clamp(1.4rem, 2.5vw, 1.9rem)', lineHeight: '1.2', fontFamily: 'Lato, sans-serif' }}>
              
              TPCM Alternatives Q&amp;A
            </h3>

            {/* Description */}
            <p className="text-sm leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.72)' }}>
              Hear directly from the Timber Point Alternatives team on how alternatives can benefit your portfolio, our process and how we partner with advisor&apos;s to deliver institutional grade solutions.
            </p>

            {/* Speaker attribution row */}
            <div className="flex items-center gap-4">
              {/* Circular headshot */}
              <div
                  className="flex-shrink-0 rounded-full overflow-hidden"
                  style={{ width: '52px', height: '52px', border: '2px solid #c9a84c' }}>
                
                <img
                    src="/assets/images/Manish2_cropped-1780960342572.png"
                    alt="Manish Shah, JD — Head of Alternatives at Timber Point Capital Management"
                    className="w-full h-full object-cover" />
                
              </div>
              {/* Name + title */}
              <div>
                <p className="font-bold text-white text-sm">Manish Shah, JD</p>
                <p className="text-xs font-medium" style={{ color: '#c9a84c' }}>
                  Head of Alternatives
                </p>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      {/* Gold border line below video section */}
      <div className="w-full" style={{ borderBottom: '2px solid #c9a84c' }} />
      {/* Video Modal */}
      {videoOpen &&
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
        onClick={() => setVideoOpen(false)}>
        
          <div
          className="relative w-full max-w-4xl mx-4"
          style={{ aspectRatio: '16/9' }}
          onClick={(e) => e?.stopPropagation()}>
          
            <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/yD3secdqhBc?autoplay=1"
            title="TPCM Alternatives Q&A"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full" />
          
            <button
            className="absolute -top-10 right-0 text-white text-3xl font-light hover:text-[#c9a84c] transition-colors"
            onClick={() => setVideoOpen(false)}
            aria-label="Close video">
            
              ×
            </button>
          </div>
        </div>
      }
      {/* FAQ Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-12">
          {/* FAQ Accordion */}
          <div>
            {faqs?.map((faq, i) =>
            <div
              key={i}
              className="border-b"
              style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              
                <button
                className="w-full text-left py-5 flex items-start justify-between gap-4 group"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}>
                
                  <span
                  className="font-semibold text-sm leading-snug group-hover:text-[#c9a84c] transition-colors"
                  style={{ color: openIndex === i ? '#c9a84c' : '#ffffff' }}>
                  
                    {faq?.q}
                  </span>
                  <span
                  className="flex-shrink-0 w-5 h-5 flex items-center justify-center transition-transform duration-200"
                  style={{
                    color: '#c9a84c',
                    transform: openIndex === i ? 'rotate(45deg)' : 'rotate(0deg)'
                  }}>
                  
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </span>
                </button>
                {openIndex === i &&
              <div className="pb-5">
                    <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      {faq?.a}
                    </p>
                  </div>
              }
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}